import * as Yup from "yup";
import { ValidationArgsType } from "./types";
import { getImageDimensionInfo, getKBInBytes, getKBInMB, isEmpty, isValidHtmlColor } from "../utils";
import {
  DEFAULT_MAX_ALLOWED_EMAIL_IN_EMAIL_LIST,
  MAX_DEFAULT_EMAIL_LENGTH,
  MAX_DEFAULT_IMAGE_SIZE_KB,
  MAX_DEFAULT_NUMBER_VALUE,
  MAX_DEFAULT_PHONE_LENGTH,
  MAX_DEFAULT_TEXT_FIELD_LENGTH,
  MAX_DEFAULT_TEXTAREA_LENGTH,
  MAX_DEFAULT_URL_LENGTH,
  MIN_DEFAULT_PHONE_LENGTH,
  MIN_DEFAULT_TEXTAREA_LENGTH,
  MIN_NUMBER_VALUE
} from "../utils/constants";
import {
  onlyDigitsRegex,
  onlyDigitsWithDecimalRegex,
  passwordRegex,
  phoneNumberRegex,
  validEmailRegex,
  validUrlRegex
} from "../utils/regex";
import PhoneUtil from "./fields/phoneNumberField/PhoneUtil";
import { Copies } from "../hooks/useCopies";
import OperatorType from "./renderer/enums/OperatorType";
import { ValidationError } from "yup";
import { TestContext } from "yup/lib/util/createValidation";
import { ApiResourceAsset } from "shared/types/ApiResourceAsset";
import { isInValidAspectRatio } from "./core/assetUpload/util";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import isRegExp from "lodash/isRegExp";
import isString from "lodash/isString";
import * as yup from "yup";
import { getYupTestForQuery } from "../../apps/console/modules/program/configuration/selection/fsl/detail/service";

export default class Validation {
  /**
   * @interface ValidationArgsType
   * @param props
   */
  static yupIsReq(props: ValidationArgsType) {
    return props.required
      ? Yup.string()
          .trim()
          .required(props.requiredMsg || props.copies.get("required"))
      : Yup.string().trim();
  }

  /**
   * Required validation for multiselect
   * @interface ValidationArgsType
   * @param props
   */
  static yupIsReqArr(props: ValidationArgsType) {
    return props.required
      ? Yup.array().min(1, props.copies.get(props.copyKey || "select.atleast.one.option"))
      : Yup.array();
  }

  /**
   * @interface ValidationArgsType
   * @param props
   */
  static yupImgIsReq(props: ValidationArgsType) {
    return props.required ? Yup.mixed().required(props.requiredMsg || props.copies.get("required")) : Yup.mixed();
  }

  /**
   * Required validation for checkbox
   * @interface ValidationArgsType
   * @param props
   */
  static yupIsReqCheckbox(props: ValidationArgsType) {
    return props.required
      ? Yup.boolean().oneOf([true], props.requiredMsg || props.copies.get("required"))
      : Yup.boolean();
  }

  /**
   * @interface ValidationArgsType
   * @param min
   * @param max
   * @param props
   */
  static multiSelectValidation({ min = MIN_NUMBER_VALUE, max = MIN_NUMBER_VALUE, ...props }: ValidationArgsType) {
    return this.yupIsReqArr(props)
      .test("regex test", props.regexMsg || props.copies.get("invalid.data"), function (value) {
        if (isEmpty(value) || !props.regex) {
          return true;
        }
        return (
          !!value &&
          !!props.regex &&
          value.filter(item => {
            return props.regex && !new RegExp(props.regex).test(item.trim());
          }).length === 0
        );
      })
      .test(
        "min length validation",
        props.copies.get(props.minCopyKey || "select.minimum.options", { 0: min }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(min > 0 && value && value.length < min);
        }
      )
      .test(
        "max length validation",
        props.copies.get(props.maxCopyKey || "select.maximum.options", { 0: max }),
        function (value) {
          return !(max > 0 && value && value.length > max);
        }
      );
  }

  /**
   *
   * @param min
   * @param max
   * @param props
   */
  static textFieldValidation({
    min = MIN_NUMBER_VALUE,
    max = MAX_DEFAULT_TEXT_FIELD_LENGTH,
    ...props
  }: ValidationArgsType) {
    return this.yupIsReq(props)
      .test("regex test", props.regexMsg || props.copies.get("invalid.data"), function (value) {
        if (isEmpty(value) || isEmpty(props.regex)) {
          return true;
        }
        if (isString(props.regex)) return !!value && new RegExp(props.regex).test(value.trim());
        else if (isRegExp(props.regex)) return !!value && props.regex.test(value.trim());
        return false;
      })
      .test(
        "min length test",
        props.copies.get(props.minCharactersCopyKey || "minimum.characters", { 0: min }),
        function (value) {
          if (isEmpty(value)) {
            return true;
          }
          return !(value && value.trim().length < min);
        }
      )
      .max(max, props.copies.get(props.maxCharactersCopyKey || "maximum.characters", { 0: max }));
  }

  /**
   * @param min
   * @param max
   * @param props
   */
  static textAreaValidation({
    min = MIN_DEFAULT_TEXTAREA_LENGTH,
    max = MAX_DEFAULT_TEXTAREA_LENGTH,
    ...props
  }: ValidationArgsType) {
    return this.yupIsReq(props)
      .test("regex test", props.regexMsg || props.copies.get("invalid.data"), function (value) {
        if (isEmpty(value) || isEmpty(props.regex)) {
          return true;
        }
        return !!value && !!props.regex && new RegExp(props.regex).test(value.trim());
      })
      .test(
        "min length test",
        props.copies.get(props.minCharactersCopyKey || "minimum.characters", { 0: min }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(value && value.trim().length < min);
        }
      )
      .max(max, props.copies.get(props.maxCharactersCopyKey || "maximum.characters", { 0: max }));
  }

  /**
   * @param props
   */
  static emailValidation(props: ValidationArgsType) {
    return this.yupIsReq(props)
      .max(
        props.max || MAX_DEFAULT_EMAIL_LENGTH,
        props.copies.get("maximum.characters", { 0: props.max || MAX_DEFAULT_EMAIL_LENGTH })
      )
      .test("email regex test", props.copies.get("invalid.email"), function (value) {
        if (isEmpty(value)) return true;
        return !!value && validEmailRegex.test(value);
      })
      .test("regex test", props.regexMsg || props.copies.get("invalid.data"), function (value) {
        if (isEmpty(value) || isEmpty(props.regex)) {
          return true;
        }
        return !!value && !!props.regex && new RegExp(props.regex).test(value.trim());
      });
  }

  static emailValidationArray({
    min = MIN_NUMBER_VALUE,
    max = DEFAULT_MAX_ALLOWED_EMAIL_IN_EMAIL_LIST,
    ...props
  }: ValidationArgsType) {
    return this.yupIsReqArr({ copyKey: "required", ...props })
      .test(
        "minLength",
        props.copies.get(props.minCopyKey || "minimum.allowed.emails", {
          0: min
        }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(min > 0 && value && value.length < min);
        }
      )
      .max(
        max,
        props.copies.get(props.maxCopyKey || "maximum.allowed.emails", {
          0: max
        })
      )
      .test("email list regex test", async (value, { createError, path }) => {
        if (isEmpty(value) || !value?.length) return true;
        const invalidEmails = value
          .filter(item => !validEmailRegex.test(item))
          .join(", ")
          .toLowerCase();
        return invalidEmails
          ? createError({
              message: props.copies.get("invalid.email.list", { 0: invalidEmails }),
              path
            })
          : true;
      });
  }

  /**
   * @param max
   * @param props
   */
  static urlValidation({ max = MAX_DEFAULT_URL_LENGTH, ...props }: ValidationArgsType) {
    return this.yupIsReq(props)
      .test(
        "URL min length",
        props.copies.get("minimum.characters.without.spaces", { 0: props.min }),
        function (value) {
          if (isEmpty(value)) return true;
          if (!props.min) return true;
          return !(value && value.trim().length < props.min);
        }
      )
      .max(max, props.copies.get("maximum.characters.without.spaces", { 0: max }))
      .test("no space allowed", props.copies.get("invalid.url"), function (value) {
        if (isEmpty(value)) return true;
        return !!value && value.trim().indexOf(" ") === -1 && validUrlRegex.test(value.trim());
      });
  }

  /**
   * @param maxValue
   * @param props
   */
  static numberValidation({ max = MAX_DEFAULT_NUMBER_VALUE, min = MIN_NUMBER_VALUE, ...props }: ValidationArgsType) {
    // using as string intentionally for better handling
    return this.yupIsReq(props)
      .test("only digits regex test", props.copies.get("upto.two.decimal.places"), function (value) {
        if (isEmpty(value)) return true;
        return !!value && onlyDigitsWithDecimalRegex.test(value.trim());
      })
      .test(
        "min value test",
        props.copies.get("minimum.allowed.value", {
          0: min
        }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(value && parseFloat(value) < min);
        }
      )
      .test(
        "max 1 billion test",
        props.copies.get("maximum.allowed.value", {
          0: max
        }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(value && parseFloat(value) > max);
        }
      );
  }

  static integerValidation({ max = MAX_DEFAULT_NUMBER_VALUE, min = MIN_NUMBER_VALUE, ...props }: ValidationArgsType) {
    // using as string intentionally for better handling
    return this.yupIsReq(props)
      .test("only digits regex test", props.copies.get("only.digits.allowed"), function (value) {
        if (isEmpty(value)) return true;
        return !!value && onlyDigitsRegex.test(value.trim());
      })
      .test(
        "min value test",
        props.copies.get("minimum.allowed.value", {
          0: min
        }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(value && parseInt(value) < min);
        }
      )
      .test(
        "max 1 billion test",
        props.copies.get("maximum.allowed.value", {
          0: max
        }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(value && parseInt(value) > max);
        }
      );
  }

  /**
   * @description use this method to validate phone number
   * @param props
   */
  static phoneNumberFieldValidation(props: ValidationArgsType) {
    return this.yupIsReq(props)
      .test("no country code test", props.copies.get("invalid.country.code"), function (value) {
        if (isEmpty(value)) return true;
        return !!value?.startsWith("+");
      })
      .test("only digits regex test", props.copies.get("only.digits.allowed"), function (value) {
        if (isEmpty(value)) return true;
        return !!value && phoneNumberRegex.test(value.trim());
      })
      .test(
        "min value test",
        props.copies.get("minimum.required.length", {
          0: MIN_DEFAULT_PHONE_LENGTH
        }),
        function (value) {
          if (isEmpty(value)) return true;
          return !(value && value.length < MIN_DEFAULT_PHONE_LENGTH);
        }
      )
      .max(
        MAX_DEFAULT_PHONE_LENGTH,
        props.copies.get("maximum.allowed.length", {
          0: MAX_DEFAULT_PHONE_LENGTH
        })
      )
      .test("valid e164 number test", props.copies.get("invalid.phone.number"), function (value) {
        return PhoneUtil.validatePhoneNumber(value?.trim() || "", props.required);
      });
  }

  /**
   * @description - validation for captcha component
   */
  static captchaValidation(props: ValidationArgsType) {
    return this.yupIsReq(props).test("no spaces allowed", props.copies.get("spaces.not.allowed"), function (value) {
      if (isEmpty(value)) return true;
      return value?.trim().indexOf(" ") === -1;
    });
  }

  static validateRegex(props: ValidationArgsType) {
    return this.yupIsReq(props).test(
      "is a valid regular expression",
      props.copies.get("invalid.regex"),
      function (value) {
        if (isEmpty(value) && !props.required) return true;
        try {
          new RegExp(value || "");
          return true;
        } catch (e) {
          return false;
        }
      }
    );
  }

  static imageValidation({ max = MAX_DEFAULT_IMAGE_SIZE_KB, ...props }: ValidationArgsType) {
    const maxSizeInMB = getKBInMB(max);
    const validationMessage =
      maxSizeInMB > 1
        ? props.copies.get("maximum.file.size.in.mb", {
            0: maxSizeInMB
          })
        : props.copies.get("maximum.file.size.in.kb", {
            0: max
          });
    return props.required
      ? Yup.mixed()
          .test(
            "required",
            props.copies.get("required", {
              0: max
            }),
            value => {
              if ((typeof value === "string" && value.length === 0) || !value) {
                return false;
              }
              return true;
            }
          )
          .test("fileSize", validationMessage, value => {
            return typeof value === "string" ? true : value?.size <= getKBInBytes(max);
          })
      : Yup.mixed().test("fileSize", validationMessage, value =>
          typeof value === "string" || isEmpty(value) ? true : value?.size <= getKBInBytes(max)
        );
  }

  static passwordValidation(props: ValidationArgsType) {
    return this.yupIsReq(props).test("password", props.copies.get("invalid.password"), function (value) {
      if (isEmpty(value)) return false;
      return passwordRegex.test(value as string);
    });
  }

  static colorValidation(props: ValidationArgsType) {
    return this.yupIsReq(props).test("colorRegexTest", props.copies.get("invalid.color"), function (value) {
      if (isEmpty(value)) return true;
      return isValidHtmlColor(value);
    });
  }

  /**
   *
   * @param props
   */
  static dateRangeFieldValidation(props: {
    required: boolean;
    copies: Copies;
    rangeValidation: Array<{
      compareFieldKey: string;
      operator: OperatorType.GREATER_THAN | OperatorType.LESS_THAN;
      errorMessage: string;
    }>;
    shouldStopValidationPreHook?: (value: any, context: TestContext) => boolean;
  }) {
    return Yup.string()
      .trim()
      .test("date compare test", function (date, context) {
        if (props.shouldStopValidationPreHook?.(date, context)) {
          return true;
        }
        if (!date && props.required) {
          return context.createError({ message: props.copies.get("required") });
        }
        if (!date) {
          return true;
        }
        const errors: Array<ValidationError> = [];
        props.rangeValidation.forEach(item => {
          const dateToCompare = context.parent[item.compareFieldKey];
          if (!dateToCompare) {
            return true;
          }
          switch (item.operator) {
            case OperatorType.GREATER_THAN:
              if (DateTimeHelper.compareDates(date, dateToCompare) !== 1) {
                errors.push(context.createError({ message: item.errorMessage }));
              }
              break;
            case OperatorType.LESS_THAN:
              if (DateTimeHelper.compareDates(date, dateToCompare) !== -1) {
                errors.push(context.createError({ message: item.errorMessage }));
              }
              break;
          }
        });
        return errors.length > 0 ? new ValidationError(errors) : true;
      });
  }

  static assetUploadValidation(props: ValidationArgsType) {
    return this.yupIsReq(props).test("assetUploadValidation", async (value, { createError, path }) => {
      const fieldValue = value as unknown as ApiResourceAsset;
      if (fieldValue?.url && props.assetConfig) {
        const dimension = await getImageDimensionInfo(fieldValue.url);
        const hasError = isInValidAspectRatio({
          fileDimension: dimension,
          recommendedDimension: props.assetConfig.dimension
        });
        return hasError
          ? createError({
              message: props.copies.get("incorrect.aspect.ratio.error.msg"),
              path
            })
          : true;
      }
      return true;
    });
  }

  static eligibilityCriteriaValidation(props: ValidationArgsType) {
    return yup
      .object()
      .nullable()
      .test("recepientCriteriaTest", getYupTestForQuery(props.copies, { isOptional: !props.required }));
  }
}
